import { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { Linking } from 'react-native';

import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import useEffectOnce from 'hooks/use-effect-once';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import { ClickEventComponentNames, CustomEventNames } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLaunchDarklyClientReady } from 'state/launchdarkly/use-launch-darkly-client';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { isActiveWalmartUser } from 'state/loyalty/hooks/utils/walmart';
import { toError } from 'utils/errors';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { useLoyaltyWalmart } from '../use-loyalty-walmart';
import { WalmartLogo } from '../walmart-logo';

import { UnlinkWalmartUserModal } from './unlink-walmart-user-modal';
import { WalmartOfferItem } from './walmart-offer-item';

const TIME_TO_REFETCH_LOYALTY_USER_AFTER_UNLINK = 3000;

export const WalmartOffer: FC<React.PropsWithChildren<{}>> = () => {
  const enableWalmartPlusAnnualTrialOffer = useFlag(
    LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER
  );

  const enableWalmartPlusMonthlyTrialOffer = useFlag(
    LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER_MONTHLY
  );

  const enableWalmartPlusTrialOffer =
    enableWalmartPlusAnnualTrialOffer || enableWalmartPlusMonthlyTrialOffer;

  const enableWalmartUnlinkFlow = useFlag(LaunchDarklyFlag.ENABLE_UNLINK_WALMART_FLOW);
  const enableWalmartPlusQuarterlyOffer = useFlag(
    LaunchDarklyFlag.ENABLE_WALMART_PLUS_QUARTERLY_OFFER
  );
  const ldClientReady = useLaunchDarklyClientReady();

  const { closeModalAuth } = useAuthContext();

  const router = useRouter();

  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);
  const [isVerifyingUnlink, setIsVerifyingUnlink] = useState<boolean>(false);

  const {
    loyaltyUser,
    loading: isLoadingLoyaltyUser,
    refetch: refetchLoyaltyUser,
  } = useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const isWalmartUser = isActiveWalmartUser(loyaltyUser);

  const { data, loading } = useLoyaltyWalmart();
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const handleActivateAccount = () => {
    logButtonClick({
      attributes: {
        name: CustomEventNames.ACTIVATE_WALMART_PLUS_ACCOUNT,
        component: ClickEventComponentNames.BUTTON,
      },
    });
    router.navigate(routes.walmart);
  };

  const handleRefetchLoyaltyUserAfterUnlink = useCallback(
    async (retry: boolean = true) => {
      setIsVerifyingUnlink(true);
      try {
        await refetchLoyaltyUser();

        if (!isWalmartUser) {
          removeAppStateChangeListeners();
          setIsVerifyingUnlink(false);
        } else if (retry) {
          // If the user is still linked, we try to verify once again after a few seconds.
          setTimeout(
            () => handleRefetchLoyaltyUserAfterUnlink(false),
            TIME_TO_REFETCH_LOYALTY_USER_AFTER_UNLINK
          );
        } else {
          setIsVerifyingUnlink(false);
        }
      } catch (error) {
        logger.error({
          error: toError(error),
          message: 'Error refreshing loyalty user after Walmart unlink',
        });
        removeAppStateChangeListeners();
        setIsVerifyingUnlink(false);
      }
    },
    [isWalmartUser, refetchLoyaltyUser, removeAppStateChangeListeners]
  );

  const handleRedirectToUnlink = useCallback(() => {
    const unlinkWalmartSanityUrl = data?.walmartUnlinkUrl;

    if (unlinkWalmartSanityUrl) {
      /**
       * Before redirecting the user, we add an event listener to detect when the user
       * returns to the app. This way, we can fetch the loyaltyUser again and verify that
       * the user was successfully unlinked.
       */
      addAppStateChangeListeners(handleRefetchLoyaltyUserAfterUnlink);
      Linking.openURL(unlinkWalmartSanityUrl);
    }
  }, [addAppStateChangeListeners, data?.walmartUnlinkUrl, handleRefetchLoyaltyUserAfterUnlink]);

  useEffectOnce(() => {
    return () => {
      // TODO: Consider closing the modal using state in the useAuth context on mount.
      closeModalAuth();
    };
  });

  const handleClickWalmartOfferItemCard = () => {
    if (!isWalmartUser) {
      return;
    }

    router.navigate(routes.walmart);
  };

  return (
    <>
      {!ldClientReady ? (
        <LoyaltyLoader />
      ) : (
        !loading && (
          <WalmartOfferItem
            name={t('partnershipWalmartTitle')}
            description={
              enableWalmartPlusQuarterlyOffer
                ? t('partnershipWalmartDescriptionQuarterlyOffer')
                : t('partnershipWalmartDescription')
            }
            descriptionL2={
              enableWalmartPlusTrialOffer && !isWalmartUser
                ? enableWalmartPlusMonthlyTrialOffer
                  ? t('walmartPlusMembershipDiscountMonthly')
                  : t('walmartPlusMembershipDiscount')
                : undefined
            }
            image={<WalmartLogo image={data?.logo?.locale} />}
            activateButtonLabel={t('activate')}
            onPartnershipActivate={handleActivateAccount}
            onhandleClickWalmartOfferItemCard={handleClickWalmartOfferItemCard}
            isLoadingUser={isLoadingLoyaltyUser}
            isWalmartUser={isWalmartUser}
            showUnlinkButton={enableWalmartUnlinkFlow}
            unlinkButtonLabel={t('walmartDeactivateButton')}
            onUnlinkUser={() => setShowUnlinkModal(true)}
            isUnlinkingUser={isVerifyingUnlink}
          />
        )
      )}
      <UnlinkWalmartUserModal
        open={showUnlinkModal}
        onConfirm={handleRedirectToUnlink}
        onClose={() => setShowUnlinkModal(false)}
      />
    </>
  );
};
